import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  getProductoPublicById,
  getProductosPublic,
} from "../../services/public/ProductosPublic";
import { fileURL } from "../../utils/fileURL";
import ProductCarousel from "../../components/store/ProductCarousel";
import { useConfiguracionTiendaPublic } from "./hooks/useConfiguracionTiendaPublic";
import "./ProductosDetalles.css";
import { useCart } from "./hooks/useCart";

const getStock = (p) => {
  const candidates = [
    p?.cantidad_disponible,
    p?.stock,
    p?.cantidad,
    p?.cantidad_stock,
    p?.qty,
    p?.qty_disponible,
    p?.cantidad_disponible_total,
  ];

  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const getCategoryId = (p) =>
  p?.categoria_producto_id ?? p?.categoria_id ?? p?.categoria;

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.data,
    payload?.rows,
    payload?.items,
    payload?.productos,
    payload?.results,
  ];

  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { add } = useCart();

  // Query string de los filtros con los que se llegó al producto (se propaga
  // entre detalles de productos relacionados). Permite que "Regresar" devuelva
  // a la lista con la categoría/marca seleccionada.
  const fromSearch = location.state?.fromSearch ?? "";
  const backToList = `/store/productos${fromSearch}`;
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [showZoom, setShowZoom] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const { configuracionTienda, formatMoney } = useConfiguracionTiendaPublic();

  const loadData = useCallback(async () => {
    setLoading(true);
    setPageError(null);

    try {
      const [prodRes] = await Promise.allSettled([getProductoPublicById(id)]);

      if (prodRes.status !== "fulfilled" || !prodRes.value) {
        setPageError("No pudimos cargar este producto. Intenta de nuevo.");
        setProduct(null);
        setRelated([]);
        return;
      }

      const p = prodRes.value;
      setProduct(p);

      const allRaw = await getProductosPublic();
      const all = toArray(allRaw);
      const catId = getCategoryId(p);
      const currentId = Number(p?.producto_id ?? id);

      const relatedCalculated = all
        .filter((x) => String(getCategoryId(x)) === String(catId))
        .filter((x) => Number(x?.producto_id) !== currentId)
        .filter((x) => getStock(x) > 0)
        .slice(0, 12);

      setRelated(relatedCalculated);
    } catch (err) {
      setPageError("No pudimos cargar este producto. Intenta de nuevo.");
      setRelated([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const anyOverlayOpen = showZoom || showConfirm || isAdding;
    if (!anyOverlayOpen) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
    };
  }, [showZoom, showConfirm, isAdding]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setShowZoom(false);
      setShowConfirm(false);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [id]);

  const coreCharge = useMemo(() => {
    const n = Number(product?.core_charge);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }, [product?.core_charge]);

  const brandName = useMemo(() => {
    const v = String(product?.marca_nombre ?? "").trim();
    return v.length ? v : null;
  }, [product?.marca_nombre]);

  const categoryName = useMemo(() => {
    const v = String(product?.categoria_nombre ?? "").trim();
    return v.length ? v : null;
  }, [product?.categoria_nombre]);

  const subcategoriasTexto = useMemo(() => {
    const v = String(product?.subcategorias_texto ?? "").trim();
    return v.length ? v : null;
  }, [product?.subcategorias_texto]);

  const descriptionHtml = useMemo(() => {
    const raw = String(product?.descripcion ?? "").trim();
    if (!raw) return null;

    return raw
      .replace(/<p><\/p>/gi, "")
      .replace(/<div><br><\/div>/gi, "<br />")
      .replace(/(<br\s*\/?>\s*){3,}/gi, "<br /><br />")
      .trim();
  }, [product?.descripcion]);

  const pesoValue = useMemo(() => {
    const n = Number(product?.peso);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }, [product?.peso]);

  const pesoAbrev = useMemo(() => {
    const a = product?.unidad_peso_abreviatura ?? product?.abreviatura;
    const s = String(a ?? "").trim();
    return s || null;
  }, [product?.unidad_peso_abreviatura, product?.abreviatura]);

  const available = useMemo(() => getStock(product), [product]);
  const outOfStock = !loading && available <= 0;

  const relatedInStock = useMemo(() => {
    return (related || []).filter((p) => getStock(p) > 0);
  }, [related]);

  const imageUrls = useMemo(() => {
    const raw =
      Array.isArray(product?.imagenes) && product.imagenes.length > 0
        ? product.imagenes
        : product?.url_image
        ? [product.url_image]
        : [];
    return raw.map((u) => fileURL(u)).filter(Boolean);
  }, [product?.imagenes, product?.url_image]);

  const safeImageIndex =
    imageUrls.length > 0 ? Math.min(activeImageIndex, imageUrls.length - 1) : 0;
  const mainImageSrc = imageUrls.length > 0 ? imageUrls[safeImageIndex] : null;

  useEffect(() => {
    setActiveImageIndex((i) =>
      imageUrls.length === 0 ? 0 : Math.min(i, imageUrls.length - 1)
    );
  }, [imageUrls.length]);

  // Rotación automática de la galería: cambia la imagen cada 3 s para dar
  // sensación de "sistema vivo". Se pausa mientras el zoom está abierto.
  useEffect(() => {
    if (imageUrls.length <= 1 || showZoom) return;

    const intervalId = setInterval(() => {
      setActiveImageIndex((i) => (i + 1) % imageUrls.length);
    }, 3000);

    return () => clearInterval(intervalId);
  }, [imageUrls.length, showZoom]);

  const clampQty = useCallback(
    (value) => {
      let next = Number(value);
      if (!Number.isFinite(next) || next < 1) next = 1;
      if (!outOfStock && available > 0 && next > available) next = available;
      return next;
    },
    [available, outOfStock]
  );

  const handleQtyChange = (value) => setQty(clampQty(value));
  const decQty = () => setQty((q) => clampQty(q - 1));
  const incQty = () => setQty((q) => clampQty(q + 1));

  const handleOpenZoom = () => {
    if (outOfStock || !mainImageSrc) return;
    setShowZoom(true);
  };

  const handleAddToCart = async () => {
    if (outOfStock || isAdding) return;

    try {
      setIsAdding(true);
      await new Promise((res) => setTimeout(res, 450));

      // Se pasa el producto tal cual (con su precio base y datos de oferta);
      // addToCart decide el precio cobrado y conserva el original.
      const productForCart = {
        ...product,
        core_charge: coreCharge ?? 0,
      };

      add(productForCart, qty);
      setShowConfirm(true);
    } finally {
      setIsAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="product-detail-scope">
        <div className="product-detail__skeleton">
          <div className="product-detail__skel product-detail__skel--img" />
          <div className="product-detail__skel product-detail__skel--lines" />
        </div>
      </div>
    );
  }

  if (pageError || !product) {
    return (
      <div className="product-detail-scope">
        <div className="product-detail">
          <div className="product-detail__error">
            <h2>Ups</h2>
            <p>{pageError || "Producto no encontrado."}</p>
            <button
              type="button"
              className="product-detail__add-btn product-detail__add-btn--primary"
              onClick={loadData}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const enOferta = Boolean(product?.en_oferta);
  const precioBase = Number(product?.precio_unitario || 0);
  const price = enOferta ? Number(product?.precio_efectivo || precioBase) : precioBase;
  const totalNow = price * qty + (coreCharge ? coreCharge * qty : 0);

  return (
    <div className="product-detail-scope">
      <div className="product-detail">
        {isAdding && (
          <div className="product-detail__overlay" role="status" aria-live="polite">
            <div className="product-detail__spinner" />
            <p>Agregando producto al carrito…</p>
          </div>
        )}

        <button
          type="button"
          className="product-detail__back-btn"
          onClick={() => navigate(backToList)}
        >
          <span className="product-detail__back-icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="product-detail__back-text">Regresar a Productos</span>
        </button>

        <div className="product-detail__main">
          <div className="product-detail__left">
            <div className="product-detail__gallery-wrap">
              <div
                className={`product-detail__gallery ${
                  outOfStock ? "product-detail__gallery--disabled" : ""
                } ${!mainImageSrc ? "product-detail__gallery--empty" : ""}`}
                role="button"
                tabIndex={outOfStock || !mainImageSrc ? -1 : 0}
                onClick={handleOpenZoom}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !outOfStock && mainImageSrc) {
                    handleOpenZoom();
                  }
                }}
                aria-disabled={outOfStock || !mainImageSrc ? "true" : "false"}
                title={
                  outOfStock
                    ? "No disponible"
                    : mainImageSrc
                    ? "Clic para hacer zoom"
                    : "Sin imagen"
                }
              >
                {mainImageSrc ? (
                  <img
                    key={mainImageSrc}
                    src={mainImageSrc}
                    alt={product.nombre}
                    className="product-detail__gallery-img--live"
                  />
                ) : (
                  <div className="product-detail__no-image" aria-hidden>
                    <span className="product-detail__no-image-ico"> </span>
                    <span className="product-detail__no-image-txt">Sin imagen</span>
                  </div>
                )}

                <span
                  className={`product-detail__stock-badge ${
                    outOfStock
                      ? "product-detail__stock-badge--out"
                      : "product-detail__stock-badge--in"
                  }`}
                >
                  {outOfStock ? "Agotado" : "Disponible"}
                </span>

                {!outOfStock && mainImageSrc && (
                  <span className="product-detail__zoom-hint">
                    Clic para hacer zoom
                  </span>
                )}
              </div>

              {imageUrls.length > 1 && (
                <div className="product-detail__thumbs" role="tablist" aria-label="Galería de imágenes">
                  {imageUrls.map((src, i) => (
                    <button
                      key={`${src}-${i}`}
                      type="button"
                      role="tab"
                      aria-selected={i === safeImageIndex}
                      className={`product-detail__thumb ${
                        i === safeImageIndex ? "product-detail__thumb--active" : ""
                      }`}
                      onClick={() => setActiveImageIndex(i)}
                      title={`Imagen ${i + 1} de ${imageUrls.length}`}
                    >
                      <img src={src} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {(brandName || categoryName || subcategoriasTexto || pesoValue !== null) && (
              <div className="product-detail__specs-card">
                {brandName && (
                  <div className="product-detail__spec-row">
                    <span className="product-detail__spec-key">Marca</span>
                    <span className="product-detail__spec-value">{brandName}</span>
                  </div>
                )}

                {categoryName && (
                  <div className="product-detail__spec-row">
                    <span className="product-detail__spec-key">Categoría</span>
                    <span className="product-detail__spec-value">{categoryName}</span>
                  </div>
                )}

                {subcategoriasTexto && (
                  <div className="product-detail__spec-row">
                    <span className="product-detail__spec-key">Subcategorías</span>
                    <span className="product-detail__spec-value">
                      {subcategoriasTexto}
                    </span>
                  </div>
                )}

                {product?.modelo && (
                  <div className="product-detail__spec-row">
                    <span className="product-detail__spec-key">Modelo</span>
                    <span className="product-detail__spec-value">{product.modelo}</span>
                  </div>
                )}

                {pesoValue !== null && (
                  <div className="product-detail__spec-row">
                    <span className="product-detail__spec-key">Peso</span>
                    <span className="product-detail__spec-value">
                      {pesoValue}
                      {pesoAbrev ? ` ${pesoAbrev}` : ""}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="product-detail__info">
            <div className="product-detail__head">
              <h1 className="product-detail__title">{product.nombre}</h1>
              {product?.no_parte && (
                <p className="product-detail__partno">SKU: {product.no_parte}</p>
              )}
            </div>

            <div className="product-detail__buy-card">
              <div className="product-detail__price-row">
                <div>
                  {price > 0 && (
                    enOferta ? (
                      <div className="product-detail__price-offer">
                        <span className="product-detail__price-badge">
                          −{product?.descuento_porcentaje}%
                        </span>
                        <span className="product-detail__price-old">
                          {formatMoney(precioBase)}
                        </span>
                        <span className="product-detail__price product-detail__price--offer">
                          {formatMoney(price)}
                        </span>
                      </div>
                    ) : (
                      <div className="product-detail__price">{formatMoney(price)}</div>
                    )
                  )}

                  {coreCharge !== null && (
                    <div className="product-detail__core">
                      Core charge: <strong>{formatMoney(coreCharge)}</strong>
                    </div>
                  )}
                </div>

                <div
                  className={`product-detail__availability-pill ${
                    outOfStock
                      ? "product-detail__availability-pill--out"
                      : "product-detail__availability-pill--in"
                  }`}
                >
                  {outOfStock
                    ? "Agotado"
                    : `${available} unidad${available > 1 ? "es" : ""} disponibles`}
                </div>
              </div>

              <div className="product-detail__qty-row">
                <span className="product-detail__label">Cantidad</span>

                <div className="product-detail__stepper">
                  <button
                    type="button"
                    className="product-detail__step"
                    onClick={decQty}
                    disabled={outOfStock || qty <= 1}
                    aria-label="Disminuir cantidad"
                  >
                    −
                  </button>

                  <input
                    type="number"
                    min="1"
                    max={available}
                    value={qty}
                    disabled={outOfStock}
                    onChange={(e) => handleQtyChange(e.target.value)}
                    aria-label="Cantidad"
                  />

                  <button
                    type="button"
                    className="product-detail__step"
                    onClick={incQty}
                    disabled={outOfStock || qty >= available}
                    aria-label="Aumentar cantidad"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                className="product-detail__add-btn product-detail__add-btn--primary"
                disabled={outOfStock}
                onClick={handleAddToCart}
                title={outOfStock ? "No disponible" : "Agregar al carrito"}
              >
                {outOfStock ? "Agotado" : "Agregar al carrito"}
              </button>

              {totalNow > 0 && (
                <div className="product-detail__mini-total">
                  <span>Total estimado</span>
                  <strong>{formatMoney(totalNow)}</strong>
                </div>
              )}

              {product.url_documento && (
                <a
                  className="product-detail__pdf-btn"
                  href={fileURL(product.url_documento)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                Documentación técnica
                </a>
              )}

            </div>
          </div>
        </div>

        {descriptionHtml && (
          <div className="product-detail__desc">
            <h3>Descripción</h3>
            <div
              className="product-detail__desc-text"
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          </div>
        )}

        <div className="product-detail__related">
          <h3>Productos relacionados</h3>
          {relatedInStock.length > 0 ? (
            <ProductCarousel
              products={relatedInStock}
              configuracionTienda={configuracionTienda}
              onSelect={(p) => {
                if (getStock(p) <= 0) return;
                navigate(`/store/product/${p.producto_id}`, {
                  state: { fromSearch },
                });
              }}
            />
          ) : (
            <p className="product-detail__muted">
              No hay productos relacionados disponibles en este momento.
            </p>
          )}
        </div>

        {showZoom && mainImageSrc && (
          <div
            className="product-detail__image-zoom"
            role="dialog"
            aria-modal="true"
            aria-label="Vista previa de la imagen"
            onClick={() => setShowZoom(false)}
          >
            <div
              className="product-detail__zoom-card"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="product-detail__zoom-close"
                onClick={() => setShowZoom(false)}
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
              <img src={mainImageSrc} alt={product.nombre} />
            </div>
          </div>
        )}

        {showConfirm && (
          <div
            className="product-detail__modal"
            role="dialog"
            aria-modal="true"
            aria-label="Producto agregado al carrito"
            onClick={() => setShowConfirm(false)}
          >
            <div
              className="product-detail__modal-card"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Producto agregado al carrito</h3>
              <p>¿Qué te gustaría hacer ahora?</p>

              <div className="product-detail__modal-actions">
                <button
                  className="product-detail__modal-secondary"
                  onClick={() => setShowConfirm(false)}
                >
                  Seguir comprando
                </button>

                <button
                  className="product-detail__modal-primary"
                  onClick={() => navigate("/store/cart")}
                >
                  Ir al carrito
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductDetail;